import { Box } from "@mui/system";

interface FuturisticHeaderProps {
  /**
   * Een lijst met korte teksten die oplichten in de header.
   * Bijv: ["CSS ALIGN", "COMBINATORS", "PSEUDO"]
   */
  items?: string[];
}

export const FuturisticHeader = ({
  items = ["INSERT", "TOPICS", "VIA", "PROPS"], // Default fallback
}: FuturisticHeaderProps) => {
  // GLR Kleuren
  const primaryColor = "#39ff14"; // Fel groen
  const secondaryColor = "#00f3ff"; // Cyaan
  const tertiaryColor = "#bc13fe"; // Paars

  // Achtergrond data string (statisch)
  const matrixData = "01001000 01010100 01001101 01001100 3C 2F 3E 7B";

  return (
    <Box
      sx={{
        width: "100%",
        height: "50vh",
        backgroundColor: "#050505",
        position: "relative",
        overflow: "hidden",
        borderRadius: "4px",

        boxShadow: `0 0 15px rgba(46, 254, 64, 0.8)`,
        marginBottom: 4,
      }}
    >
      <style>
        {`
          @keyframes glowPulse {
            0% { opacity: 0.1; filter: drop-shadow(0 0 0px ${primaryColor}); }
            40% { opacity: 1; filter: drop-shadow(0 0 8px ${primaryColor}); }
            100% { opacity: 0.1; filter: drop-shadow(0 0 0px ${primaryColor}); }
          }
          
          @keyframes matrixPulse {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 0.5; }
          }

          .tech-text {
            font-family: 'Courier New', monospace;
            font-weight: bold;
            letter-spacing: 1px;
            text-transform: uppercase;
            fill: ${primaryColor};
            font-size: 14px;
          }

           @media (min-width: 600px) {
             .tech-text { font-size: 16px; letter-spacing: 2px; }
           }
        `}
      </style>

      <svg
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        style={{ position: "absolute", left: 0, top: 0 }}
      >
        <defs>
          <linearGradient id="gradGreen" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop
              offset="0%"
              style={{ stopColor: primaryColor, stopOpacity: 0 }}
            />
            <stop
              offset="50%"
              style={{ stopColor: primaryColor, stopOpacity: 0.8 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: primaryColor, stopOpacity: 0 }}
            />
          </linearGradient>
        </defs>

        {/* 1. Grid Background */}
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path
            d="M 40 0 L 0 0 0 40"
            fill="none"
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="1"
          />
        </pattern>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* 2. Matrix Data (Background noise) */}
        <text
          x="50%"
          y="40"
          className="tech-text"
          style={{
            fontSize: "12px",
            animation: "matrixPulse 6s infinite",
            fill: secondaryColor,
          }}
          textAnchor="middle"
        >
          {matrixData} {matrixData} {matrixData}
        </text>

        {/* 3. Dynamic Items (Props) */}
        {items.map((item, index) => {
          // Bereken positie: Verdeel 100% door aantal items.
          // Zet item in het midden van zijn 'vakje'.
          const widthPerItem = 100 / items.length;
          const xPos = widthPerItem * index + widthPerItem / 2;

          // Bereken delay: Zorgt voor niet-lineair knipperen (beetje random feel)
          // index * 1.3 zorgt dat ze niet op volgorde gaan
          const delay = `${(index * 1.3) % 4}s`;

          return (
            <text
              key={index}
              x={`${xPos}%`}
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="tech-text"
              style={{
                animation: "glowPulse 4s ease-in-out infinite",
                animationDelay: delay,
              }}
            >
              {item}
            </text>
          );
        })}

        {/* 4. Decorative Waves */}
        <path
          d="M0,200 Q300,100 600,200 T1200,200 T1800,200 V250 H0 Z"
          fill="none"
          stroke={tertiaryColor}
          strokeWidth="2"
          opacity="0.5"
        >
          <animate
            attributeName="d"
            dur="12s"
            repeatCount="indefinite"
            values="
               M0,200 Q300,100 600,200 T1200,200 T1800,200 V250 H0 Z;
               M0,200 Q300,250 600,200 T1200,200 T1800,200 V250 H0 Z;
               M0,200 Q300,100 600,200 T1200,200 T1800,200 V250 H0 Z"
          />
        </path>

        <path
          d="M0,180 Q250,120 500,180 T1000,180 T1500,180"
          fill="none"
          stroke="url(#gradGreen)"
          strokeWidth="3"
        />
      </svg>

      {/* Vignette Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle, rgba(0,0,0,0) 50%, rgba(0,0,0,0.9) 100%)",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};
